import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/features/auth/hooks/useSession';
import { useConversation } from '@/app/providers/ConversationContext';
import { useDailyBrief } from '@/app/providers/DailyBriefContext';
import { useModulePane } from '@/app/providers/ModulePaneContext';
import { useObjectSelection } from '@/app/providers/ObjectSelectionContext';
import { listWorkspaceModules } from '@/lib/api/endpoints/workspaces';
import { resolveWorkspaceId, buildConversationPath } from '@/lib/workspace-routing';
import { queryKeys } from '@/lib/query/keys';
import type { ActivityItem } from './useActivities';

/**
 * Opening an activity item routes the user to the thing it's about — the SAME
 * navigation the Daily Brief uses (openPane → navigate(conversationPath) →
 * selectObject), so notifications are integrated with the rest of Xavior rather
 * than a parallel path.
 *
 * Resolution order:
 *  1. `objectType` (+ `objectId`) → open that object's module, deep-link to the
 *     conversation, and highlight the exact object when one is referenced.
 *  2. else `conversationId` → open the related conversation.
 *  3. else not openable (`canOpen` is false; the row renders non-interactive).
 */

// objectType → the module `type`(s) that render it (first match in the
// workspace wins). Mirrors the brief's findModuleId lookups.
const OBJECT_MODULE_TYPES: Record<string, string[]> = {
  task: ['task_list', 'kanban'],
  event: ['event_list', 'calendar', 'timeline'],
  contact: ['contact_list'],
  place: ['place_list', 'places', 'map'],
  note: ['notes'],
  email: ['email'],
};

// Object types ObjectSelectionContext can highlight (its `type` union).
type SelectableType =
  | 'task'
  | 'note'
  | 'doc'
  | 'event'
  | 'contact'
  | 'place'
  | 'asset'
  | 'stat'
  | 'email';
const SELECTABLE = new Set<string>([
  'task',
  'note',
  'doc',
  'event',
  'contact',
  'place',
  'asset',
  'stat',
  'email',
]);

export interface OpenActivityItem {
  /** True when the item routes somewhere (has a resolvable module or a conversation). */
  canOpen: (item: ActivityItem) => boolean;
  /** Route to the item's object/conversation. No-op when nothing is resolvable. */
  openItem: (item: ActivityItem) => void;
}

/**
 * A daily-brief "ready" activity is discriminated by its unique `open_brief`
 * action — NOT by `verb === 'briefing_ready'`. User-scheduled *briefing
 * reminders* (reminder-cron) also carry `verb='briefing_ready'`, but their
 * actions are navigate/snooze/dismiss, so keying on the action prevents a fired
 * briefing reminder from wrongly opening the Daily Brief modal.
 */
const isBriefActivity = (item: ActivityItem): boolean =>
  item.actions?.some((a) => a.action === 'open_brief') ?? false;

export function useOpenActivityItem(): OpenActivityItem {
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const { activeConversation, allConversations } = useConversation();
  const dailyBrief = useDailyBrief();
  const { openPane } = useModulePane();
  const { selectObject } = useObjectSelection();
  const navigate = useNavigate();

  const modulesQ = useQuery({
    queryKey: queryKeys.modules.list(workspaceId),
    queryFn: () => listWorkspaceModules(token, workspaceId),
    enabled: !!token && !!workspaceId,
    staleTime: 60_000,
  });

  const findModuleId = (types: string[]): string | undefined =>
    modulesQ.data?.find((m) => !m.archivedAt && types.includes(m.type))?.id;

  const resolveModuleId = (item: ActivityItem): string | undefined => {
    const moduleTypes = item.objectType ? OBJECT_MODULE_TYPES[item.objectType] : undefined;
    return moduleTypes ? findModuleId(moduleTypes) : undefined;
  };

  const canOpen = (item: ActivityItem): boolean => {
    if (isBriefActivity(item)) return true;
    return Boolean(resolveModuleId(item)) || Boolean(item.conversationId);
  };

  const openItem = (item: ActivityItem): void => {
    // A brief opens the brief window directly (no workspace context needed), so
    // handle it before the workspace guard — matching canOpen (which returns true
    // for briefs unconditionally); otherwise a brief row can render clickable yet
    // no-op while workspaceId is momentarily absent.
    if (isBriefActivity(item)) { dailyBrief.open(); return; }
    if (!workspaceId) return;
    const moduleId = resolveModuleId(item);
    const convId = activeConversation?.id ?? allConversations[0]?.id;

    if (moduleId && convId) {
      // SSOT (ADR 028): record the open intent before navigating so the URL
      // guard holds for a not-yet-attached module.
      openPane(moduleId);
      navigate(buildConversationPath(workspaceId, convId, { moduleId }));
      window.dispatchEvent(
        new CustomEvent('xavior:force-module-focus', { detail: { moduleId } }),
      );
      if (item.objectId && item.objectType && SELECTABLE.has(item.objectType)) {
        selectObject(item.objectType as SelectableType, item.objectId);
      }
      return;
    }

    if (item.conversationId) {
      navigate(buildConversationPath(workspaceId, item.conversationId));
    }
  };

  return { canOpen, openItem };
}
